import type { Moneda } from "@tora/shared";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { ChipMoneda, Importe } from "@/components/money/importe";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

export interface KpiProps {
  etiqueta: string;
  monto: number;
  moneda: Moneda;
  /** `md` = text-lg (grillas de 4 o mas), `lg` = text-2xl (saldo principal). */
  tamano?: "md" | "lg";
  /** Signo o estado del saldo, como badge al lado de la etiqueta. Nunca cambia el color del numero. */
  estado?: {
    texto: string;
    variant: Extract<BadgeProps["variant"], "success" | "warning" | "destructive" | "secondary">;
  };
  /** "cotizacion 1.234,50", "3 cheques", equivalente en pesos. */
  nota?: ReactNode;
  icono?: LucideIcon;
  /** Barra lateral de moneda. Default true. */
  acento?: boolean;
  /** Antepone "+" a los positivos (saldo a favor). */
  signoExplicito?: boolean;
  className?: string;
}

/**
 * Un saldo o total con nombre. Etiqueta ARRIBA siempre, chip de moneda en la
 * cabecera y el numero sin chip pero con el color de SU moneda: un "a favor"
 * en verde y un "debe" en rojo dejarian de distinguirse por moneda, que es lo
 * que el azul/terracota garantiza (MASTER seccion 3.2). El estado se dice con
 * un Badge al lado.
 */
export function Kpi({
  etiqueta,
  monto,
  moneda,
  tamano = "lg",
  estado,
  nota,
  icono,
  acento = true,
  signoExplicito = false,
  className,
}: KpiProps): JSX.Element {
  return (
    <Card
      as="section"
      padding="compacto"
      acento={acento ? moneda : "none"}
      data-moneda={moneda}
      className={cn("flex flex-col gap-1", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <TituloSeccion nivel="etiqueta" icono={icono}>
          {etiqueta}
        </TituloSeccion>
        <span className="flex shrink-0 items-center gap-1.5">
          {estado ? <Badge variant={estado.variant}>{estado.texto}</Badge> : null}
          <ChipMoneda moneda={moneda} />
        </span>
      </div>
      <Importe
        monto={monto}
        moneda={moneda}
        codigo={false}
        tamano={tamano === "lg" ? "xl" : "lg"}
        signoExplicito={signoExplicito}
        className="font-semibold"
      />
      {nota ? <p className="text-xs text-muted-foreground">{nota}</p> : null}
    </Card>
  );
}
